import {
    QueryHookOptions,
    DocumentNode,
    OperationVariables,
    TypedDocumentNode,
} from "@apollo/client";
import { Session } from "@app/shared/types";
import { Box, BoxProps } from "@mui/system";
import LoadingPage from "features/pages/LoadingPage";
import NotFoundPage from "features/pages/NotFoundPage";
import { useFetchDataAndKeepUpdated } from "hooks/useFetchDataAndKeepUpdated";
import { DateTime } from "luxon";
import smoothscroll from "smoothscroll-polyfill";
import { EventList } from "./EventList";
import { EventListByDay } from "./EventListByDay";
import { useScrollToEvent } from "./useScrollToEvent";
smoothscroll.polyfill();

export interface EventListLoaderProps<TData = any, TVariables = OperationVariables>
    extends BoxProps {
    eventsQuery: DocumentNode | TypedDocumentNode<TData, TVariables>;
    queryOptions?: QueryHookOptions<TData, TVariables>;
    elementBefore?: React.ReactNode;
    elementAfter?: React.ReactNode;
    currentServerTime: DateTime;
    splitEventsByDay?: boolean;
    limitDisplayedEvents?: number;
    showSpecificDate?: boolean;
    filterEvents?: (events: Session) => boolean;
}

export const EventListLoader = (props: EventListLoaderProps) => {
    const {
        eventsQuery,
        queryOptions,
        elementBefore,
        currentServerTime,
        splitEventsByDay,
        elementAfter,
        limitDisplayedEvents,
        showSpecificDate,
        filterEvents,
        ...others
    } = props;

    const { data, loading, error } = useFetchDataAndKeepUpdated({
        query: eventsQuery,
        options: queryOptions,
        pollIntervalInMinutes: 30,
    });

    let events = data ? (Object.values(data)[0] as Session[]) : undefined;

    useScrollToEvent(events);

    if (error) {
        return <NotFoundPage />;
    }

    if (loading) {
        return <LoadingPage />;
    }

    if (!events || events.length < 1) {
        return null;
    }

    if (filterEvents) {
        events = events.filter(filterEvents);
    }

    const displaySeeMoreButton = events.length > (limitDisplayedEvents || 0);

    if (limitDisplayedEvents) {
        events = events.slice(0, limitDisplayedEvents);
    }

    return (
        <Box {...others}>
            <>
                {elementBefore}
                {splitEventsByDay ? (
                    <EventListByDay
                        events={events}
                        currentServerTime={currentServerTime}
                        showSpecificDate={showSpecificDate}
                    />
                ) : (
                    <EventList
                        events={events}
                        currentServerTime={currentServerTime}
                        showSpecificDate={showSpecificDate}
                    />
                )}
                {displaySeeMoreButton && elementAfter}
            </>
        </Box>
    );
};
